import api from "../../api/cofocusAPI";

const ItemAPI = {
  /**
   *
   * @param {String} UID User's ID
   * @param {Object} itemData
   * @param {String} itemData.name
   * @param {String} itemData.spaceID
   * @param {String} itemData.type
   * @returns
   */
  async createOneItem(UID, itemData) {
    if (!UID || !itemData.name || !itemData.spaceID || !itemData.type) {
      return { success: false, error: "Required data not provided for request." };
    }
    return api.getResponseData({
      url: `api/items/${UID}/new`,
      method: "post",
      data: { UID, itemData },
      withCredentials: true,
    });
  },

  async completeItemByID(UID, itemID) {
    if (!UID || !itemID) return { success: false, error: "Required data not provided for request." };
    return api.getResponseData({
      url: `api/items/${UID}/complete`,
      method: "post",
      data: { UID, itemID },
      withCredentials: true,
    });
  },

  async uncompleteItemByID(UID, itemID) {
    if (!UID || !itemID) return { success: false, error: "Required data not provided for request." };
    return api.getResponseData({
      url: `api/items/${UID}/uncomplete`,
      method: "post",
      data: { UID, itemID },
      withCredentials: true,
    });
  },

  /**
   * Sends request to server to add one item to trash by ID
   *
   * @param {String} itemID — A single item's ID
   * @returns {Object} — Result of request
   */
  async trashOneItem(UID, itemID) {
    if (!UID || !itemID) return { success: false, error: "Required data not provided for request." };
    return api.getResponseData({
      url: `api/items/${UID}/trash`,
      method: "post",
      data: { itemID },
      withCredentials: true,
    });
  },

  /**
   * Sends request to server to remove one item from trash by ID
   *
   * @param {String} itemID — A single item's ID
   * @returns {Object} — Result of request
   */
  async untrashOneItem(UID, itemID) {
    if (!UID || !itemID) return { success: false, error: "Required data not provided for request." };
    return api.getResponseData({
      url: `api/items/${UID}/untrash`,
      method: "post",
      data: { itemID },
      withCredentials: true,
    });
  },

  /**
   * Sends request to server to delete one item by ID.
   *
   * @param {String} itemID — A single item's ID
   * @returns {Object} — Result of request
   */
  async deleteOneItem(UID, itemID) {
    if (!UID || !itemID) return { success: false, error: "Required data not provided for request." };
    return api.getResponseData({
      url: `api/items/${UID}/delete`,
      method: "delete",
      params: { itemID },
      withCredentials: true,
    });
  },

  /**
   * Loads more items from the database if any.
   *
   * @param {String} UID
   * @param {Object} options
   * @returns {Object}
   *
   * !NB It's a post request because passing complex data and don't want to use a query converter for now.
   */
  async getItems(UID, spaceID, options = {}, filters = {}) {
    if (!UID || !spaceID) throw new Error("Required data not provided for request.");
    const result = await api.getResponseData({
      url: `api/items/${UID}/load`,
      method: "post",
      data: { UID, spaceID, options, filters },
      withCredentials: true,
    });
    if (!result || !result.success) throw new Error("Failed to get data.");
    return result.data;
  },

  async getItemsByContextID(UID, spaceID, contextID, options = {}, filters = {}) {
    if (!UID || !spaceID || !contextID) throw new Error("Required data not provided for request.");
    const skip = options.skip ?? 0;
    const limit = options.limit ?? 0;
    const sort = options.sort ?? 0;
    filters = 0;
    const result = await api.getResponseData({
      url: `api/items/${UID}/loadContext?spaceID=${spaceID}&contextID=${contextID}&skip=${skip}&limit=${limit}&sort=${sort}&filters=${filters}`,
      method: "get",
      withCredentials: true,
    });
    if (!result || !result.success) throw new Error("Failed to get data.");
    return result.data;
  },

  async getOneItemByID(UID, itemID) {
    if (!UID || !itemID) throw new Error("Required data not provided for request.");
    return await api.getResponseData({
      url: `api/items/${UID}/item?itemID=${itemID}`,
      method: "get",
      withCredentials: true,
    });
  },

  async preloadManyItemsByIDs(UID, itemIDs) {
    if (!UID || !itemIDs || !itemIDs.length) throw new Error("Required data not provided for request.");
    return await api.getResponseData({
      url: `api/items/${UID}/preload`,
      method: "post",
      data: { UID, itemIDs },
      withCredentials: true,
    });
  },

  /**
   * Sends request to server API to update the content field of an item.
   *
   * @param {String} UID User's ID
   * @param {String} itemID The item's ID
   * @param {String} newValue New value
   * @returns
   */
  async updateItemContentByID(UID, itemID, newValue) {
    if (!UID || !itemID || !newValue) return { success: false, error: "Required data not provided for request." };
    return api.getResponseData({
      url: `api/items/${UID}/updateContent`,
      method: "post",
      data: { UID, itemID, newValue },
      withCredentials: true,
    });
  },

  /**
   * Generic item updater by ID of item
   *
   * @param {String} UID User's ID
   * @param {String} itemID The item's ID
   * @param {Object} item Item with updated values inside
   * @returns
   */
  async updateItemByID(UID, itemID, item) {
    if (!UID || !itemID || !item) return { success: false, error: "Required data not provided for request." };
    return api.getResponseData({
      url: `api/items/${UID}/update`,
      method: "post",
      data: { UID, itemID, item },
      withCredentials: true,
    });
  },

  /**
   *
   * @param {String} UID User's own ID
   * @param {String} itemID Item ID to add property to
   * @param {Object} prop Property to add to item
   * @param {String} prop.name Name of property
   * @param {String} prop.type Type of property
   * @param {String} prop.contextID ID of context
   * @param {String} prop.contextType Type of context
   * @param {String} prop.metadata Optional metadata for prop, such as color, icon, etc
   * @returns
   */
  async addOnePropertyToItem(UID, itemID, prop) {
    /** Ensure requierd metadata exists to be able to create tag */
    if (!UID || !itemID || !prop || !prop.name || !prop.type || !prop.contextID || !prop.contextType) {
      return { success: false, error: "Required data not provided for request." };
    }
    return api.getResponseData({
      url: `api/items/${UID}/addProperty`,
      method: "post",
      data: { UID, itemID, prop },
      withCredentials: true,
    });
  },
};

export default ItemAPI;
